import { hasDefinedValue, isNonEmpty } from '@sgme/fp';
import { Layout } from 'components/missed-deal-feedback/components/layout';
import { PercentWithUnit } from 'components/shared/Percent/PercentWithUnit';
import type { CompetitionParameterFeedback } from 'components/shared/WithErrorTooltip/model';
import { useAppSelector } from 'hooks/useAppSelector';
import { PropsWithChildren } from 'react';
import { FormattedMessage } from 'react-intl';
import { FormIdProps } from 'shared/props';
import { CouponDefType, MissedFeedbackCompetitionParameter } from 'state/missed-deal-feeedback/feedback/model';
import {
  getCompetitionParametersFeedbacks,
  getCompetitionParametersReferential,
  getFeedbackComment,
  getFeedbackIsClientNotTraded,
  getFeedbackIsPriceEnhanced,
  getFeedbackNumberOfIssuers,
  getFeedbackSgPosition,
} from 'state/missed-deal-feeedback/selectors';
import { snakeCaseToWords } from 'utils/string';

import { FormGroup } from './form-group';
import { PriceEnhancedReadonly } from './price-enhanced-readonly';
import { SgPositioningReadonly } from './sg-positioning-readonly';

type ParameterProps = PropsWithChildren<{
  competitionParameterFeedback: CompetitionParameterFeedback;
  referentialParameter: MissedFeedbackCompetitionParameter;
}>;
function Parameter({ competitionParameterFeedback, referentialParameter }: ParameterProps) {
  const sgLabel = `SG ${snakeCaseToWords(referentialParameter.label ?? '')}`;
  const bestOfferLabel = `Best offer ${snakeCaseToWords(competitionParameterFeedback.label ?? '')}`;

  const buildUnitWithFrequency = (frequency?: CouponDefType) =>
    `${referentialParameter.unit!} ${frequency ?? ''}`.trim();

  return (
    <Layout.Row>
      <FormGroup className="col-6" inputId={`sg-${referentialParameter.label}-group`} labelText={sgLabel}>
        <PercentWithUnit
          id={`sg-${referentialParameter.label}-group`}
          e2e={`sg-offer-${referentialParameter.label!}`}
          unit={buildUnitWithFrequency(referentialParameter.couponDefType)}
          isReadOnly
          value={referentialParameter.value!}
          precision={4}
        />
      </FormGroup>
      <FormGroup
        className="col-6"
        inputId={`best-offer-${competitionParameterFeedback.label}-group`}
        labelText={bestOfferLabel}
      >
        <PercentWithUnit
          e2e={`best-offer-${competitionParameterFeedback.label!}`}
          id={`best-offer-${competitionParameterFeedback.label}-group`}
          className="text-info border-bottom border-info"
          unitClassName="text-info"
          unit={buildUnitWithFrequency(competitionParameterFeedback.bestOfferFrequency!)}
          isReadOnly
          value={competitionParameterFeedback.bestOfferValue!}
          precision={4}
        />
      </FormGroup>
    </Layout.Row>
  );
}
const useReadonlyForm = (tabId: string) => {
  return {
    comment: useAppSelector(s => getFeedbackComment(s, tabId)),
    isClientNotTraded: useAppSelector(s => getFeedbackIsClientNotTraded(s, tabId)),
    competitionParametersReferential: useAppSelector(s => getCompetitionParametersReferential(s, tabId) ?? []).filter(
      hasDefinedValue('label'),
    ),
    competitionParametersFeedbacks: useAppSelector(s => getCompetitionParametersFeedbacks(s, tabId) ?? []).filter(
      hasDefinedValue('label'),
    ),
    isPriceEnhanced: useAppSelector(s => getFeedbackIsPriceEnhanced(s, tabId)),
    sgPosition: useAppSelector(s => getFeedbackSgPosition(s, tabId)),
    numberOfIssuers: useAppSelector(s => getFeedbackNumberOfIssuers(s, tabId)),
  };
};
export function MissedDealFeedbackReadonlyForm({ formId }: FormIdProps) {
  const {
    comment,
    isClientNotTraded,
    competitionParametersReferential,
    competitionParametersFeedbacks,
    isPriceEnhanced,
    sgPosition,
    numberOfIssuers,
  } = useReadonlyForm(formId);
  const findInReferential = (label?: string) => competitionParametersReferential.find(p => p.label === label)!;

  const hasSgPositioning = sgPosition !== undefined && numberOfIssuers !== undefined;
  const hasOptionalInfos = isClientNotTraded || isPriceEnhanced || hasSgPositioning;
  return (
    <>
      {competitionParametersFeedbacks.map(feedback => (
        <Parameter
          key={`missedDealFeedbackParameter${feedback.label}`}
          competitionParameterFeedback={feedback}
          referentialParameter={findInReferential(feedback.label)}
        />
      ))}
      {hasOptionalInfos && (
        <Layout.Block>
          {hasSgPositioning && <SgPositioningReadonly sgPosition={sgPosition} numberOfIssuers={numberOfIssuers} />}
          {isClientNotTraded && (
            <div className="text-wrap font-italic">
              <FormattedMessage id="missedDealFeedback.clientTraded" />
            </div>
          )}
          {isPriceEnhanced && <PriceEnhancedReadonly formId={formId} />}
        </Layout.Block>
      )}
      {isNonEmpty(comment) && (
        <Layout.Row>
          <FormGroup inputId="Comment" label="missedDealFeedback.comment">
            <p data-e2e="feedback-comment" className="border-bottom border-info text-info">
              {comment}
            </p>
          </FormGroup>
        </Layout.Row>
      )}
    </>
  );
}
